import { motion } from "framer-motion";
import { useState } from "react";
import { Mail, MapPin, Clock, CheckCircle } from "lucide-react";
import { FaWhatsapp, FaTiktok, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { useLangStore } from "@/lib/langStore";
import { translations } from "@/lib/i18n";
import Layout from "@/components/Layout";

export default function ContactPage() {
  const { lang } = useLangStore();
  const t = translations[lang];

  const [form, setForm] = useState({
    name: "",
    email: "",
    project: "",
    budget: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const BOT_TOKEN = "8777327279:AAFQdCYA9Yl51O2jWzgH_GZZae5tjwPFBc8";
    const CHAT_ID = "2110461498";

    const text = `
🚀 *New Project Inquiry*
------------------------
👤 *Name:* ${form.name}
📧 *Email:* ${form.email}
📁 *Project:* ${form.project || "Not specified"}
💰 *Budget:* ${form.budget || "Not specified"}
📝 *Message:*
${form.message}
    `;

    try {
      const telegramPromise = fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chat_id: CHAT_ID,
          text: text,
          parse_mode: "Markdown",
        }),
      });

      const emailPromise = fetch("https://formsubmit.co/ajax/hany.design77@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          project: form.project,
          budget: form.budget,
          message: form.message,
          _subject: "🚀 New Project Inquiry from Portfolio",
        }),
      });

      const [tgRes, emailRes] = await Promise.all([telegramPromise, emailPromise]);

      if (!tgRes.ok || !emailRes.ok) throw new Error("Failed to send to one or more services");

      setSubmitted(true);
    } catch (error) {
      console.error("Submission Error:", error);
      alert(lang === "ar" ? "حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى." : "Error sending message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const projectTypes = {
    en: ["Basics (300-500 EGP)", "Professional (500-700 EGP)", "Premium (800-1200 EGP)", "Additional Services"],
    ar: ["الأساسيات (300-500 ج.م)", "الاحترافي (500-700 ج.م)", "المتميز (800-1200 ج.م)", "خدمات إضافية"],
  };
  const budgets = {
    en: ["150 - 300 EGP", "300 - 500 EGP", "500 - 700 EGP", "800 - 1200 EGP", "1200+ EGP"],
    ar: ["150 - 300 ج.م", "300 - 500 ج.م", "500 - 700 ج.م", "800 - 1200 ج.م", "+1200 ج.م"],
  };

  return (
    <Layout>
      <div className="pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-14">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-primary font-semibold text-sm uppercase tracking-wider mb-2"
            >
              ✦ {lang === "en" ? "Let's Talk" : "لنتحدث"}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl lg:text-5xl font-extrabold text-foreground mb-3"
            >
              {t.contact.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-muted-foreground text-lg max-w-xl mx-auto"
            >
              {t.contact.subtitle}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            {/* Info sidebar */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="lg:col-span-2 space-y-5"
            >
              {[
                {
                  icon: <Mail size={20} />,
                  label: lang === "en" ? "Email" : "البريد الإلكتروني",
                  value: "hany.design77@gmail.com",
                },
                {
                  icon: <FaWhatsapp size={20} />,
                  label: lang === "en" ? "WhatsApp" : "واتساب",
                  value: "+201015346757",
                  link: "https://wa.me/201015346757",
                },
                {
                  icon: <MapPin size={20} />,
                  label: lang === "en" ? "Location" : "الموقع",
                  value: lang === "en" ? "Available Worldwide" : "متاح عالمياً",
                },
                {
                  icon: <Clock size={20} />,
                  label: lang === "en" ? "Response Time" : "وقت الرد",
                  value: lang === "en" ? "Within 24 hours" : "خلال 24 ساعة",
                },
              ].map((item, i) => (
                <motion.a
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  href={item.link || "#"}
                  target={item.link ? "_blank" : undefined}
                  rel={item.link ? "noopener noreferrer" : undefined}
                  className={`flex items-start gap-4 p-5 rounded-2xl border border-border bg-card transition-all duration-200 ${
                    item.link ? "hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 group" : ""
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {item.icon}
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground font-medium mb-0.5">{item.label}</div>
                    <div className="font-semibold text-foreground text-sm">{item.value}</div>
                  </div>
                </motion.a>
              ))}

              {/* Social links */}
              <div className="p-5 rounded-2xl border border-border bg-card">
                <p className="text-sm font-semibold text-foreground mb-3">{t.contact.orContact}</p>
                <div className="flex gap-3">
                  {[
                    { label: "WhatsApp", short: <FaWhatsapp size={18} />, link: "https://wa.me/201015346757" },
                    { label: "TikTok", short: <FaTiktok size={18} />, link: "https://tiktok.com/@mo_lord315" },
                    { label: "LinkedIn", short: <FaLinkedinIn size={18} />, link: "https://www.linkedin.com/in/hany-design-786110402?utm_source=share_via&utm_content=profile&utm_medium=member_android" },
                    { label: "Instagram", short: <FaInstagram size={18} />, link: "https://www.instagram.com/mo1_hany?igsh=cGU4OGd3ZHZoN2hl" },
                  ].map((s) => (
                    <a
                      key={s.label}
                      href={s.link || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={s.label}
                      className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary/15 hover:text-primary transition-colors text-xs font-bold"
                    >
                      {s.short}
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="lg:col-span-3"
            >
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-10 rounded-2xl border border-border bg-card text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-extrabold text-foreground mb-2">{t.contact.successTitle}</h3>
                  <p className="text-muted-foreground">{t.contact.successMsg}</p>
                </motion.div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="p-6 lg:p-8 rounded-2xl border border-border bg-card space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        {t.contact.nameLabel} *
                      </label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder={t.contact.namePlaceholder}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        {t.contact.emailLabel} *
                      </label>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder={t.contact.emailPlaceholder}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                  </div>

                  {/* Project type */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {t.contact.projectLabel}
                    </label>
                    <select
                      name="project"
                      value={form.project}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    >
                      <option value="">{t.contact.projectPlaceholder}</option>
                      {(lang === "ar" ? projectTypes.ar : projectTypes.en).map((pt, i) => (
                        <option key={i} value={pt}>
                          {pt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Budget */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {t.contact.budgetLabel}
                    </label>
                    <select
                      name="budget"
                      value={form.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    >
                      <option value="">{t.contact.budgetPlaceholder}</option>
                      {(lang === "ar" ? budgets.ar : budgets.en).map((b, i) => (
                        <option key={i} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {t.contact.messageLabel} *
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder={t.contact.messagePlaceholder}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-bold text-base hover:bg-primary/90 active:scale-[0.98] transition-all duration-200 shadow-xl shadow-primary/25 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? t.contact.sending : t.contact.sendBtn}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
